import React, { useState } from 'react';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, MessageCircleIcon, MailOpenIcon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
const WHATSAPP_NUMBER = '919823446128';
const EMAIL_ADDRESS = 'swastikinfra900@gmail.com';
type FormState = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};
type FormErrors = Partial<Record<keyof FormState, string>>;
const emptyForm: FormState = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
};
const ContactSection = () => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [sentVia, setSentVia] = useState<'whatsapp' | 'email' | null>(null);
  const contactDetails = [{
    icon: MapPinIcon,
    title: 'Address',
    details: ['Swastik Infra Developers', 'House No. 1006, Bolinj, Sopara Road,', 'Next to Bharat Gas, Opp. Karmale Talav,', 'Bolinj, Virar(W) 401203']
  }, {
    icon: PhoneIcon,
    title: 'Phone',
    details: ['Tejas P. Vasa', '+91 9823446128', '+91 8208381247']
  }, {
    icon: PhoneIcon,
    title: 'Phone',
    details: ['Hitesh Jain', '+91 9175953453']
  }, {
    icon: PhoneIcon,
    title: 'Phone',
    details: ['Bandish P. Vasa', '+91 8208659398']
  }, {
    icon: MailIcon,
    title: 'Email',
    details: [EMAIL_ADDRESS]
  }, {
    icon: ClockIcon,
    title: 'Working Hours',
    details: ['Monday - Saturday: 9:00 AM - 6:00 PM', 'Sunday: By Appointment Only']
  }];
  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = 'Please enter your name';
    if (!form.phone.trim()) {
      next.phone = 'Please enter your phone number';
    } else if (form.phone.replace(/\D/g, '').length < 10) {
      next.phone = 'Enter a valid 10-digit phone number';
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'Enter a valid email address';
    }
    if (!form.message.trim()) next.message = 'Please write a short message';
    return next;
  };
  const handleChange = (field: keyof FormState, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
    setSentVia(null);
  };
  const buildPlainText = () => ['New enquiry from the Swastik Infra Developers website', '', `Name: ${form.name.trim()}`, `Phone: ${form.phone.trim()}`, form.email.trim() ? `Email: ${form.email.trim()}` : null, `Subject: ${form.subject.trim() || 'General Enquiry'}`, '', 'Message:', form.message.trim()].filter(line => line !== null).join('\n');
  const submit = (channel: 'whatsapp' | 'email') => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const body = buildPlainText();
    if (channel === 'whatsapp') {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(body)}`, '_blank', 'noopener,noreferrer');
    } else {
      const subject = form.subject.trim() ? `Website Enquiry: ${form.subject.trim()}` : 'Website Enquiry — Swastik Infra Developers';
      window.location.href = `mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    setSentVia(channel);
  };
  const inputClass = (field: keyof FormState) => `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${errors[field] ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'}`;
  return <section id="contact" className="py-20 bg-white" data-id="element-244" data-mp="13j21c70jwsgk">
      <div className="container mx-auto px-4 lg:px-8" data-id="element-245" data-mp="17l6ruwvpd8yz">
        <div className="text-center mb-16" data-id="element-246" data-mp="ivu4dn7c484t">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-247" data-mp="iuizfk25kuc4">
            Contact Us
          </h2>
          <div className="w-20 h-1 bg-blue-800 mx-auto mb-6" data-id="element-248" data-mp="dxirf3lqthid"></div>
          <p className="max-w-3xl mx-auto text-gray-600 text-lg" data-id="element-249" data-mp="8wuz8els2umk">
            Get in touch with our team for inquiries about our projects or to
            schedule a site visit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-250" data-mp="3w3f8q4tftb8b">
          <div className="lg:col-span-1" data-id="element-251" data-mp="3rbxahoistor1">
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm h-full" data-id="element-252" data-mp="3ravnqmsf7dth">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-253" data-mp="3mbrdrtxo9c23">
                Get In Touch
              </h3>
              <div className="space-y-8" data-id="element-254" data-mp="3heiwrwo9fe2f">
                {contactDetails.map((item, index) => <div key={index} className="flex" data-id="element-255" data-mp="3cd37fsf1ule8" data-mpi={index}>
                    <div className="flex-shrink-0 mr-4" data-id="element-256" data-mp="38crnkhuigp3t">
                      <div className="p-3 bg-blue-100 rounded-full" data-id="element-257" data-mp="38db6a0rzjhiw">
                        <item.icon className="h-5 w-5 text-blue-800" data-id="element-258" data-mp="1nvig8vxuvpkw" />
                      </div>
                    </div>
                    <div data-id="element-259" data-mp="1rv16864jaqul">
                      <h4 className="text-lg font-medium text-gray-900 mb-1" data-id="element-260" data-mp="1ryf534dzohxa">
                        {item.title}
                      </h4>
                      {item.details.map((detail, idx) => <p key={idx} className="text-gray-600" data-id="element-261" data-mp="21s5s04ugwkav" data-mpi={idx}>
                          {detail}
                        </p>)}
                    </div>
                  </div>)}
              </div>

              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="mt-8 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors" data-id="element-262" data-mp="1e4fl0arly4tj">
                <MessageCircleIcon className="h-4 w-4" data-id="element-263" data-mp="qdiifolpcjuy" />
                Chat on WhatsApp
              </a>
            </div>
          </div>

          <div className="lg:col-span-2" data-id="element-264" data-mp="edbo48b0i6nk">
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm h-full" data-id="element-265" data-mp="eck6y2i5wbgt">
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-266" data-mp="s4qb0q81irzw">
                Send Us a Message
              </h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-267" data-mp="n6ouod2016s4">
                Your enquiry is delivered straight to our team on WhatsApp at
                +91 98234 46128 — usually answered the same working day.
              </p>

              <form onSubmit={e => {
              e.preventDefault();
              submit('whatsapp');
            }} noValidate data-id="element-268" data-mp="3rlonnzmsv5gt">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-269" data-mp="4gyypmmi2eym">
                  <div data-id="element-270" data-mp="1krunfg9gq8lp">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-271" data-mp="1kv8licb3fvnc">
                      Your Name <span className="text-red-500" data-id="element-272" data-mp="2xq4j46bho73q">*</span>
                    </label>
                    <input type="text" id="name" value={form.name} onChange={e => handleChange('name', e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} className={inputClass('name')} placeholder="John Doe" data-id="element-273" data-mp="32rk4jzhkwfmn" />
                    {errors.name && <p id="name-error" className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-274" data-mp="37p2ent7c10w8">
                        <AlertCircleIcon className="h-3 w-3" data-id="element-275" data-mp="48vc5q0nl9p1" />
                        {errors.name}
                      </p>}
                  </div>

                  <div data-id="element-276" data-mp="252zxs35hztxv">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-277" data-mp="253i0nimo16er">
                      Email Address
                    </label>
                    <input type="email" id="email" value={form.email} onChange={e => handleChange('email', e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} className={inputClass('email')} placeholder="john@example.com" data-id="element-278" data-mp="1vdyhebv1jajd" />
                    {errors.email && <p id="email-error" className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-279" data-mp="12p146n5os3xh">
                        <AlertCircleIcon className="h-3 w-3" data-id="element-280" data-mp="3aa9mawo1kpck" />
                        {errors.email}
                      </p>}
                  </div>

                  <div data-id="element-281" data-mp="3ec4k3r6rkkll">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-282" data-mp="3jaxz5o1p8jnv">
                      Phone Number <span className="text-red-500" data-id="element-283" data-mp="3o3r5uhgqa22l">*</span>
                    </label>
                    <input type="tel" id="phone" value={form.phone} onChange={e => handleChange('phone', e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'phone-error' : undefined} className={inputClass('phone')} placeholder="+91 98765-43210" data-id="element-284" data-mp="3t0hjky528lku" />
                    {errors.phone && <p id="phone-error" className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-285" data-mp="2zfaq19gcxca2">
                        <AlertCircleIcon className="h-3 w-3" data-id="element-286" data-mp="39cyhnqio23o0" />
                        {errors.phone}
                      </p>}
                  </div>

                  <div data-id="element-287" data-mp="3e9p1moxldjep">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-288" data-mp="3idmwuojsqa2h">
                      Subject
                    </label>
                    <input type="text" id="subject" value={form.subject} onChange={e => handleChange('subject', e.target.value)} className={inputClass('subject')} placeholder="Enquiry about Viral Shantivan" data-id="element-289" data-mp="3s47ua8nqh8jt" />
                  </div>

                  <div className="md:col-span-2" data-id="element-290" data-mp="1ug42mb69b4rc">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-291" data-mp="1udhsq4vw8d5y">
                      Your Message <span className="text-red-500" data-id="element-292" data-mp="1lffw01j706oh">*</span>
                    </label>
                    <textarea id="message" rows={5} value={form.message} onChange={e => handleChange('message', e.target.value)} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} className={inputClass('message')} placeholder="Write your message here..." data-id="element-293" data-mp="1gh4mjrwt7y6s"></textarea>
                    {errors.message && <p id="message-error" className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-294" data-mp="2s2kwf6fs708c">
                        <AlertCircleIcon className="h-3 w-3" data-id="element-295" data-mp="u7nupiyaqgh1" />
                        {errors.message}
                      </p>}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3" data-id="element-296" data-mp="leas1pafk0hr">
                  <button type="submit" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded font-medium transition-colors" data-id="element-297" data-mp="lcre45c8y1j5">
                    <MessageCircleIcon className="h-4 w-4" data-id="element-298" data-mp="3tnuj62lsfyhp" />
                    Send via WhatsApp
                  </button>
                  <button type="button" onClick={() => submit('email')} className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-blue-800 text-blue-800 hover:bg-blue-50 rounded font-medium transition-colors" data-id="element-299" data-mp="2ghzngxfn8ym">
                    <MailOpenIcon className="h-4 w-4" data-id="element-300" data-mp="26vtmx9ad1z3m" />
                    Send via Email
                  </button>
                </div>

                {sentVia && <div role="status" className="mt-4 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" data-id="element-301" data-mp="2fs2n4q65x1qt">
                    <CheckCircle2Icon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-302" data-mp="2pqs5sl5vuq84" />
                    <span data-id="element-303" data-mp="2kp2seyv6u0gv">
                      {sentVia === 'whatsapp' ? 'WhatsApp is opening with your enquiry ready — press send there and it reaches us instantly.' : `Your email app is opening with the enquiry addressed to ${EMAIL_ADDRESS} — press send to deliver it.`}
                    </span>
                  </div>}
              </form>
            </div>
          </div>
        </div>

        <div className="mt-12 rounded-lg overflow-hidden shadow-sm h-96 bg-gray-200" data-id="element-304" data-mp="11rfz2bv1u6h2">
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1881.260910358852!2d72.79460348188876!3d19.43305609135778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7abda12a20271%3A0x5b92909708f0cd58!2sTara%20Gas%20Agency!5e0!3m2!1sen!2sin!4v1742555375234!5m2!1sen!2sin" className="w-full h-full" style={{
          border: 0
        }} allowFullScreen loading="lazy" title="Swastik Infra Developers Location" data-id="element-305" data-mp="16pzpopkl86fr"></iframe>
        </div>
      </div>
    </section>;
};
export default ContactSection;